// App.js - Main application component
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './contexts/ThemeContext';
import { SmoothScrollProvider } from './contexts/SmoothScrollContext';
import LoadingScreen from './components/LoadingScreen';
import ProfessionalHeader from './components/ProfessionalHeader';
import Footer from './components/Footer';
import CustomCursor from './components/shared/CustomCursor';
import './tailwind-output.css';
import './App.css';

// Lazy load pages for performance
const HomePage = lazy(() => import('./pages/HomePage'));
const Services = lazy(() => import('./pages/Services'));
const FinancialSolutions = lazy(() => import('./pages/FinancialSolutions'));
const SecurityServices = lazy(() => import('./pages/SecurityServices'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Resources = lazy(() => import('./pages/Resources'));
const ServerBuilder = lazy(() => import('./pages/ServerBuilder'));

// Page transition — lightweight fade + slide
function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

// Animated routes with scroll-to-top
function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
        <Route path="/financial-solutions" element={<PageTransition><FinancialSolutions /></PageTransition>} />
        <Route path="/security-services" element={<PageTransition><SecurityServices /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/resources" element={<PageTransition><Resources /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/server-builder" element={<PageTransition><ServerBuilder /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showApp, setShowApp] = useState(false);

  useEffect(() => {
    const preloadResources = async () => {
      return new Promise(resolve => {
        setTimeout(resolve, 100);
      });
    };
    preloadResources();
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
    setTimeout(() => setShowApp(true), 100);
  };

  if (isLoading) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <ThemeProvider>
      <SmoothScrollProvider>
        <Router>
          <CustomCursor />
          <ProfessionalHeader />
          <div className={`app-wrapper ${showApp ? 'show' : ''}`}>
            <div className="app">
              <main>
                <Suspense fallback={
                  <div className="min-h-screen bg-comtel-bg flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-comtel-red/30 border-t-comtel-red rounded-full animate-spin" />
                  </div>
                }>
                  <AnimatedRoutes />
                </Suspense>
              </main>
              <Footer />
            </div>
          </div>
        </Router>
      </SmoothScrollProvider>
    </ThemeProvider>
  );
}

export default App;
